'use client';

import { useCountDown } from 'ahooks';
import { useMemo } from 'react';
import { SITE } from '@/app/constants';
import { useStore } from '@/app/providers/store';
import { calculateDaysUntilUTC } from '@/app/utils';
import type { RetentionPhase, RetentionStrategyResult } from '../types';

const NONE: RetentionStrategyResult = {
  phase: 'none',
  daysToExpiry: -1,
  isEligible: false,
  countdown: 0,
  countdownHours: 0,
  countdownMinutes: 0,
  countdownSeconds: 0,
};

/**
 * 根据订阅状态计算挽留阶段
 *
 * 前置条件：
 * 1. SITE === 'china'
 * 2. auto_renew_canceled === true
 * 3. status === 'active'
 * 4. advance_renew_in_cooldown !== true
 *
 * 阶段划分：
 * - phase1: >7天（右上角通知 + 任务中心问卷 + pricing改造）
 * - phase2: <=7天 且 >1天（开屏弹窗 + 顶部公告栏 + pricing折扣）
 * - phase3: <=1天（最后一天开屏弹窗 + 顶部公告栏 + pricing折扣）
 */
export function useRetentionStrategy(): RetentionStrategyResult {
  const { subscription, isSubscriptionInitialized } = useStore((s) => ({
    subscription: s.subscription,
    isSubscriptionInitialized: s.isSubscriptionInitialized,
  }));

  const { phase, daysToExpiry, isEligible, targetDate } = useMemo(() => {
    if (SITE !== 'china') return { ...NONE, targetDate: undefined };
    if (!isSubscriptionInitialized || !subscription) return { ...NONE, targetDate: undefined };
    if (!subscription.auto_renew_canceled) return { ...NONE, targetDate: undefined };
    if (subscription.advance_renew_in_cooldown) return { ...NONE, targetDate: undefined };

    const endTimeStr = subscription.end_time ?? '';
    const days = calculateDaysUntilUTC(endTimeStr);
    if (days <= 0) return { ...NONE, targetDate: undefined };

    let p: RetentionPhase;
    if (days <= 1) {
      p = 'phase3';
    } else if (days <= 7) {
      p = 'phase2';
    } else {
      p = 'phase1';
    }

    const target =
      p === 'phase3' ? new Date(endTimeStr.endsWith('Z') ? endTimeStr : `${endTimeStr}Z`).getTime() : undefined;

    return { phase: p, daysToExpiry: days, isEligible: true, targetDate: target };
  }, [subscription, isSubscriptionInitialized]);

  const [countdown, formattedRes] = useCountDown({ targetDate });

  return {
    phase,
    daysToExpiry,
    isEligible,
    countdown: Math.floor(countdown / 1000),
    countdownHours: formattedRes.hours,
    countdownMinutes: formattedRes.minutes,
    countdownSeconds: formattedRes.seconds,
  };
}
